import React, { useCallback } from 'react';
import { devWarning, TRUNCATED_TEXT_TOOLTIP_DELAY_MS } from '@tanium/coreui-utils';
import { Box } from '@tanium/react-box';
import styled, { css as cssEmotion } from '@tanium/react-emotion-9';
import { Icon } from '@tanium/react-graphics';
import { SimpleTooltip } from '@tanium/react-tooltip';
import { useIsOverflowing } from '@tanium/react-use-is-overflowing';
import { BorderStyleProps, ColorStyleProps, LayoutStyleProps } from '@tanium/style-system';
import { Border, Fill, Text } from './themeDefinitions';

export type PillSize = 'sm' | 'lg';

export interface PillBaseProps
  extends Pick<BorderStyleProps, 'borderColor'>,
    Pick<ColorStyleProps, 'backgroundColor'>,
    Pick<ColorStyleProps, 'color'>,
    Pick<LayoutStyleProps, 'maxWidth'> {
  leadingIcon?: Icon;

  /**
   * If specified it will be used as the "key" in the pill e.g. key: value
   */
  keyForValue?: React.ReactNode;

  /**
   * Content to display in tooltip.
   */
  tooltipContent: React.ReactNode;

  /**
   * Whether the tooltip should always be shown instead of just when the content overflows.
   *
   * Defaults to `false`
   */
  alwaysEnableTooltip?: boolean;

  /**
   * If specified it will always show a tooltip on hover with the specified content.
   *
   * @deprecated Set `alwaysEnableTooltip` to "true" instead (and use `tooltipContent`)
   */
  customTooltipContent?: React.ReactNode;

  /**
   * If specified passes the ref to the main content (leading icon and pill text and the children passed into this component)
   */
  mainContentRef?: React.Ref<HTMLDivElement>;

  /**
   * Size of the pill. Use `sm` or `lg`. Defaults to `lg`.
   */
  size?: PillSize;

  /**
   * If specified it shows content after the Pill's content
   */
  trailingContent?: React.ReactNode;

  /**
   * Content to display
   */
  children?: React.ReactNode;
}

export interface PillBaseContentProps
  extends Pick<PillBaseProps, 'keyForValue' | 'leadingIcon' | 'maxWidth' | 'mainContentRef'> {
  /**
   * Will attach the ref to top level wrapper element, used for tooltip
   */
  tooltipRef?: React.Ref<HTMLDivElement>;

  /**
   * Will attach the ref to the truncated text element
   */
  overflowRef?: React.Ref<HTMLDivElement>;

  /**
   * Content to display
   */
  children?: React.ReactNode;
}

const leadingIconStyle = cssEmotion({
  marginRight: 4,
  flexShrink: 0,
});

const NoWrapText = styled('span')({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
});

const TruncatedText = styled(NoWrapText)({
  textOverflow: 'ellipsis',
});

export const PILL_DEFAULT_MAX_WIDTH = 200;

const PillBaseContent = ({
  keyForValue,
  leadingIcon: LeadingIcon,
  maxWidth = PILL_DEFAULT_MAX_WIDTH,
  mainContentRef,
  tooltipRef,
  overflowRef,
  children,
  ...otherProps
}: PillBaseContentProps) => {
  const getKeyForValue = () => {
    let keyForValueContent = null;
    if (keyForValue) {
      keyForValueContent = <Box as={NoWrapText}>{keyForValue}: </Box>;
    }
    return keyForValueContent;
  };

  const compositeRef = useCallback(
    (node: HTMLDivElement) => {
      if (typeof mainContentRef === 'function') {
        mainContentRef(node);
      }
      if (mainContentRef && 'current' in mainContentRef) {
        const mutableRef = mainContentRef as React.MutableRefObject<HTMLDivElement>;
        mutableRef.current = node;
      }

      if (typeof tooltipRef === 'function') {
        tooltipRef(node);
      }
      if (tooltipRef && 'current' in tooltipRef) {
        const mutableRef = tooltipRef as React.MutableRefObject<HTMLDivElement>;
        mutableRef.current = node;
      }
    },
    [mainContentRef, tooltipRef],
  );

  return (
    <Box
      ref={compositeRef}
      maxWidth={maxWidth}
      display="inline-flex"
      alignItems="center"
      {...otherProps}
    >
      {LeadingIcon && <LeadingIcon height={14} width={14} className={leadingIconStyle} />}
      <TruncatedText innerRef={overflowRef}>
        {getKeyForValue()}
        {children}
      </TruncatedText>
    </Box>
  );
};

export const PillBase = ({
  color = Text,
  backgroundColor = Fill,
  borderColor = Border,
  keyForValue,
  leadingIcon: LeadingIcon,
  customTooltipContent,
  alwaysEnableTooltip = !!customTooltipContent,
  tooltipContent,
  maxWidth = PILL_DEFAULT_MAX_WIDTH,
  mainContentRef,
  size = 'lg',
  trailingContent,
  children,
}: PillBaseProps) => {
  devWarning(
    !customTooltipContent,
    '`customTooltipContent` is deprecated, instead set `alwaysEnableTooltip` to "true"`',
  );

  const [isOverflowing, overflowRef] = useIsOverflowing({ updateOnResize: true });

  const getTooltipContent = () => customTooltipContent || tooltipContent;

  const shouldShowTooltip = isOverflowing || alwaysEnableTooltip;

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      lineHeight="15px"
      height={`${size === 'lg' ? 24 : 16}px`}
      fontSize={12}
      px={1}
      color={color}
      bg={backgroundColor}
      border={1}
      borderRadius="16px"
      borderColor={borderColor}
    >
      <SimpleTooltip
        delayShowMs={TRUNCATED_TEXT_TOOLTIP_DELAY_MS}
        placement="top"
        content={shouldShowTooltip && getTooltipContent()}
      >
        {({ ref }) => (
          <PillBaseContent
            mainContentRef={mainContentRef}
            tooltipRef={ref}
            overflowRef={overflowRef}
            keyForValue={keyForValue}
            leadingIcon={LeadingIcon}
            maxWidth={maxWidth}
          >
            {children}
          </PillBaseContent>
        )}
      </SimpleTooltip>
      {trailingContent}
    </Box>
  );
};
